import { api } from './api';
import type {
  DailyShloka,
  CreateDailyShlokaPayload,
  UpdateDailyShlokaPayload,
  DailyShlokaQueryParams,
} from '../types';

export interface DailyShlokaListResult {
  items: DailyShloka[];
  total: number;
}

function unwrapListResult(data: unknown): DailyShlokaListResult {
  if (Array.isArray(data)) {
    return { items: data as DailyShloka[], total: data.length };
  }
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    const list = Array.isArray(record.data)
      ? (record.data as DailyShloka[])
      : Array.isArray(record.items)
      ? (record.items as DailyShloka[])
      : Array.isArray(record.results)
      ? (record.results as DailyShloka[])
      : [];
    const total =
      typeof record.total === 'number'
        ? record.total
        : typeof record.count === 'number'
        ? record.count
        : list.length;
    return { items: list, total };
  }
  return { items: [], total: 0 };
}

/** Normalize date/today responses that may be a single object (legacy) or an array. */
function unwrapDateResult(data: unknown): DailyShloka[] {
  if (Array.isArray(data)) {
    return data as DailyShloka[];
  }
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    if (Array.isArray(record.items)) return record.items as DailyShloka[];
    if (Array.isArray(record.data)) return record.data as DailyShloka[];
    // Legacy single-object response
    if ('id' in record || 'date' in record) {
      return [data as DailyShloka];
    }
  }
  return [];
}

export const dailyShlokaApi = {
  /**
   * Fetch today's shlokas (ordered by priority). Supports timezone query.
   */
  getToday: (timezone: string = 'Asia/Kathmandu') =>
    api.get<unknown>('/daily-shloka/today', {
      params: { timezone },
    }).then((res) => ({
      ...res,
      data: unwrapDateResult(res.data),
    })),

  /**
   * Fetch all shlokas for a specific date (YYYY-MM-DD), ordered by priority.
   */
  getByDate: (date: string) =>
    api.get<unknown>(`/daily-shloka/date/${date}`).then((res) => ({
      ...res,
      data: unwrapDateResult(res.data),
    })),

  /**
   * List daily shlokas with filtering and pagination.
   */
  getAll: (params?: DailyShlokaQueryParams) =>
    api.get<unknown>('/daily-shloka', { params }).then((res) => {
      const result = unwrapListResult(res.data);
      return {
        ...res,
        data: result.items,
        total: result.total,
      };
    }),

  /**
   * Get shloka by ID.
   */
  getById: (id: number | string) =>
    api.get<DailyShloka>(`/daily-shloka/${id}`),

  /**
   * Create or upsert a daily shloka for a date.
   */
  create: (payload: CreateDailyShlokaPayload) =>
    api.post<DailyShloka>('/daily-shloka', payload),

  /**
   * Partially update a daily shloka by ID.
   */
  update: (id: number | string, payload: UpdateDailyShlokaPayload) =>
    api.patch<DailyShloka>(`/daily-shloka/${id}`, payload),

  /**
   * Upsert or update a daily shloka by date (YYYY-MM-DD).
   */
  updateByDate: (date: string, payload: UpdateDailyShlokaPayload) =>
    api.put<DailyShloka>(`/daily-shloka/date/${date}`, payload),

  /**
   * Delete a daily shloka by ID.
   */
  delete: (id: number | string) =>
    api.delete<void>(`/daily-shloka/${id}`),

  /**
   * Unique category strings used across daily shlokas (dropdown suggestions).
   */
  getCategories: () =>
    api.get<string[]>('/daily-shloka/categories').then((res) => {
      const data = res.data;
      if (Array.isArray(data)) {
        return { ...res, data: data.map(String).filter(Boolean) };
      }
      return { ...res, data: [] as string[] };
    }),
};

export default dailyShlokaApi;
